import {RubyPlayGameClientWrapper} from '@gongxigames/framework/integrations/rubyplay/RubyPlayGameClientWrapper';
import {
    CoreDI,
    inject,
    LanguageService,
    CancelStartSpinReason,
    GameClientWrapperProperties, NetworkErrors,
    Xhr,
    KeypadControlsEvents,
    UIButtons,
    CancelStartSpinData
} from '@gongxigames/framework/slot';
import {PROMOTION_DECISION_ACTION, PromotionDecision, PromotionDecisionRequest, ResponseBody} from '@gongxigames/framework/integrations/rubyplay/types';
import {Action} from 'com/gongxigames/math/core/game/enums/Action';
import {LightAndWonderAPIAdapter} from '@gongxigames/framework/integrations/lightAndWonder/LightAndWonderAPIAdapter';
import {ILightAndWonderMessageChannel} from '@gongxigames/framework/integrations/lightAndWonder/LightAndWonderInterface';
import {LightAndWonderIntegrationDI} from '@gongxigames/framework/integrations/lightAndWonder/LightAndWonderIntegrationDI';
import {HandleFreeRoundsUpdateResponse} from '@gongxigames/framework/integrations/lightAndWonder/LightAndWonderType';
import {LightAndWonderFreeRounds} from '@gongxigames/framework/integrations/lightAndWonder/LightAndWonderFreeRounds';
import {LightAndWonderErrorsHandler} from './LightAndWonderErrorsHandler';
import {IPromotionLogicInterface} from '@gongxigames/framework/promotions';

const DEFAULT_POST_HEADERS = {'Content-Type': 'application/json'};

type BettingProps = {
    bets: number[];
    wager: number;
    fractionSize: number;
};

const LW_DISABLED_BUTTON_LIST = [UIButtons.BET, UIButtons.TOTAL_BET_OPEN_BUTTON, UIButtons.AUTOPLAY];

export class LightAndWonderGameClientWrapper extends RubyPlayGameClientWrapper {
    public static NAME = 'lnw';
    protected lightAndWonderAPIAdapter!: LightAndWonderAPIAdapter;
    protected messageChannel!: ILightAndWonderMessageChannel;
    protected _promotionLogicInterface: IPromotionLogicInterface = new LightAndWonderFreeRounds();

    // Used only for fixing CLFR-6880. At the time of writing we do not know the reason 
    // for the incident, so we do not know how to reproduce it for better solution rather 
    // than this one (workaround). This variable is set on each onGameServerActionResponse.
    //
    // TODO: rework this when the root cause is identified and fixed.
    protected active_promotion_id?: string;

    @inject(CoreDI.LanguageService)
    protected languageService!: LanguageService;

    public initWrapper(properties: GameClientWrapperProperties): void {
        super.initWrapper(properties);

        this.lightAndWonderAPIAdapter = this._installer.getComponent(LightAndWonderIntegrationDI.APIAdapter);
        this._errors = this._installer.getComponent(LightAndWonderIntegrationDI.ErrorsHandler);
        this.messageChannel = this._installer.getComponent(LightAndWonderIntegrationDI.MessageChannel);
    }

    protected setupEvents(): void {
        super.setupEvents();

        this._eventManager.on('lnw.refreshBalanceRequest', this.pingBalance.bind(this), this);

        this.on('com.lnw.gcm.handleFreeRoundsUpdate', this.onHandleFreeRoundsUpdate);
    }

    public enableUI(): void {
        for (const name of LW_DISABLED_BUTTON_LIST) {
            this._eventManager.emit('General.UI.Button.ENABLE', { reason: LightAndWonderGameClientWrapper.NAME, name });
        }
    }

    public disableUI(): void {
        for (const name of LW_DISABLED_BUTTON_LIST) {
            this._eventManager.emit('General.UI.Button.DISABLE', { reason: LightAndWonderGameClientWrapper.NAME, name });
        }
    }

    public isSelectedPromoBetValid(freeRoundsUpdate: HandleFreeRoundsUpdateResponse): boolean {
        const optionsBet = freeRoundsUpdate.freeRoundsInfo['OPTIONS'][0]['BETLEVEL'];
        const {bets, wager, fractionSize} = this.getBettingProps();
        const bet = optionsBet * fractionSize / wager;

        return bets.includes(bet);
    }

    public handleInvalidBetError(): void {
        const errorHandler = this._errors as LightAndWonderErrorsHandler;

        errorHandler.handleFreeRoundsInvalidBetLevelError();
    }

    public acceptFreeRounds(succeessCB: () => void = () => {}): void {
        const theOnlyLWFreeRoundsDecision: PromotionDecision = 'accept';

        const payload: PromotionDecisionRequest = {
            key: this._key,
            action: PROMOTION_DECISION_ACTION,
            an: this.getSession().getActionNumber(),
            promotion_decision: theOnlyLWFreeRoundsDecision,
            promotion_id: this._promotionLogicInterface.getID()
        };
        const payloadStr = JSON.stringify(payload);

        Xhr.post(this._gameServerUrl, payloadStr, (body: ResponseBody) => {
            if (body.status === 'ok') {
                this._betInRegularGame = this._defaultBet;
                this.updateDefaultBet();
                this.disableUI();
                succeessCB();
            } else {
                this.onErrorRequest();
            }
        }, DEFAULT_POST_HEADERS, this.onErrorRequest.bind(this));
    }

    public onHandleFreeRoundsUpdate(_id: string | symbol, freeRoundsUpdate: HandleFreeRoundsUpdateResponse): void {
        if (!this.isSelectedPromoBetValid(freeRoundsUpdate)) {
            this.handleInvalidBetError();
        } else {
            this.updateFreeRounds(freeRoundsUpdate);
        }
    }

    public updateFreeRounds(freeRoundsUpdate: HandleFreeRoundsUpdateResponse): void {
        const isPromotionInit = freeRoundsUpdate.freeRoundsInfo['STATUS'] === 'notstarted';
        const isPromotionInitButPending = freeRoundsUpdate.freeRoundsInfo['STATUS'] === 'inprogress' && !this.active_promotion_id;
        const isPromotionInProgress = freeRoundsUpdate.freeRoundsInfo['STATUS'] === 'inprogress';
        const {bets, wager, fractionSize} = this.getBettingProps();
        const freeRoundsInterface = this._promotionLogicInterface as LightAndWonderFreeRounds;

        if (isPromotionInit || isPromotionInitButPending) {
            freeRoundsInterface.init({bets, wager, fractionSize, data: freeRoundsUpdate});
            this.acceptFreeRounds(() => {
                this.messageChannel.sendMsgToGCM('com.lnw.gcm.resume', {triggerEvent: 'handleFreeRoundsUpdate'});
            });
        } else {
            freeRoundsInterface.update({bets, wager, fractionSize, data: freeRoundsUpdate});
            this.updateDefaultBet();

            const uiFunction = isPromotionInProgress ? this.disableUI : this.enableUI;

            uiFunction.bind(this)();

            this.messageChannel.sendMsgToGCM('com.lnw.gcm.resume', {triggerEvent: 'handleFreeRoundsUpdate'});
        }
    }

    public updateDefaultBet(): void {
        let newBet = this._defaultBet;

        if (this.getPromotion().isActive()) {
            newBet = this.getPromotion().getBet();
        } else {
            newBet = this._betInRegularGame || this._defaultBet;
        }

        if (newBet === this._defaultBet) {
            return;
        }

        this._defaultBet = newBet;

        this._eventManager.emit(KeypadControlsEvents.UPDATE_KEYPAD);
    }

    public getBettingProps(): BettingProps {
        const bets = this.getSession().getGameSettings().bets;
        const wager = this._client.getEngine().getMaxWager();
        const {fractionSize} = this.currencyFormatter;

        return {bets, wager, fractionSize};
    }

    protected updateBalance(body: ResponseBody): void {
        this.handleBonusBalance(body);

        super.updateBalance(body);
    }

    protected onGameServerActionResponse(body: ResponseBody, action: Action): void {
        super.onGameServerActionResponse(body, action);

        this.sendPromoInfoRequest(body, action);
        this.handleBonusBalance(body);
        this.handleMessageTrigger(body);
    }

    protected handlePromotion(body: ResponseBody, action?: Action): void {
        // NO-OP
    }

    protected sendPromoInfoRequest(body: ResponseBody, action: Action): void {

        // Used only for fixing CLFR-6880. At the time of writing we do not know the reason 
        // for the incident, so we do not know how to reproduce it for better solution rather 
        // than this one (workaround).
        // TODO: rework this when the root cause is identified and fixed.
        this.active_promotion_id = body.data?.active_promotion_id;

        if (body.data?.integrationsPayload) {
            const {PROMOTIONS: promotions} = JSON.parse(body.data?.integrationsPayload);

            if (promotions) {
                if (action === Action.INIT) {
                    this._betInRegularGame = this._defaultBet;
                }

                const remaining = promotions.FREEROUNDS[0].OPTIONS[0].REMAININGROUNDS;
                const total = promotions.FREEROUNDS[0].OPTIONS[0].TOTALROUNDS;
                const isFirst = remaining === total;

                promotions.GAMEPLAY = isFirst ? 'pending' : 'complete';

                this.messageChannel.sendMsgToGCM('com.lnw.gcm.setPromoInfo', {PROMOTIONS: promotions});
            }
        }
    }

    protected handleBonusBalance(body: ResponseBody): void {
        if (body.data?.integrationsPayload) {
            const {BONUSBALANCE: bonusBalance, REALBALANCE: realBalance} = JSON.parse(body.data?.integrationsPayload);

            if (bonusBalance !== undefined && realBalance !== undefined) {
                this.lightAndWonderAPIAdapter.updateBonusBalanceData({bonusBalance, realBalance});
            }
        }
    }

    protected handleMessageTrigger(body: ResponseBody): void {
        if (body.data?.integrationsPayload) {
            const {MESSAGE} = JSON.parse(body.data?.integrationsPayload);

            if (MESSAGE) {
                this.messageChannel.sendMsgToGCM('com.lnw.gcm.handleMessageTrigger', {
                    msgTriggerData: MESSAGE
                });
            }
        }
    }

    protected onCancelStartSpin({reason}: CancelStartSpinData): void {
        if (reason === CancelStartSpinReason.INSUFFICIENT_FUNDS) {
            this._eventManager.emit('lnw.clientInsufficientFunds');
        }
    }

    protected onNetworkErrorShow(reason: NetworkErrors): void {
        if (reason === NetworkErrors.LOST_CONNECTION) {
            this._eventManager.emit('General.UI.DISABLE', LightAndWonderGameClientWrapper.NAME);
            this._eventManager.emit('lnw.clientLostConnection');
            this._eventManager.emitAsync('General.System.PAUSE');
        }
    }
}